import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { Tabs, TabsStrategy } from '../../components/Tabs';
import {
  PATH_RESOLVABLE_SERVICE_ADMIN,
  PATH_RESOLVABLE_SERVICE_ADMIN_EDIT,
  PATH_RESOLVABLE_SERVICE_MATERIALS,
  PATH_RESOLVABLE_SERVICE_MATERIALS_EDIT,
  PATH_RESOLVABLE_SERVICE_COSTS,
  PATH_RESOLVABLE_SERVICE_COSTS_EDIT,
  PATH_RESOLVABLE_SERVICE_GENERAL,
  PATH_RESOLVABLE_SERVICE_GENERAL_EDIT,
  PATH_RESOLVABLE_SERVICE_QUOTATION_INFO,
  PATH_RESOLVABLE_SERVICE_QUOTATION_INFO_EDIT,
  PATH_RESOLVABLE_SERVICE_VERSIONS,
  PATH_RESOLVABLE_SERVICE_DEFAULT,
} from '../../app/constants/paths.constant';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import type { ResolvableFieldSettings } from '../../components/ResolvableFields/types/resolvable-field-settings.type';

import { Admin } from './Admin';
import { Costs } from './Costs';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';
import { QuotationInfo } from './QuotationInfo';
import { Materials } from './Materials';

export const ResolvableServicePage_Tabs_ResolvableServiceBaseInterface_Fragment =
  graphql(`
    fragment ResolvableServicePage_Tabs_ResolvableServiceBaseInterface_Fragment on ResolvableServiceBaseInterface {
      _id
      ...ResolvableServicePage_GeneralInfo_ResolvableServiceBaseInterface_Fragment
      ...ResolvableServicePage_QuotationInfo_ResolvableServiceBaseInterface_Fragment
      ...ResolvableServicePage_Costs_ResolvableServiceBaseInterface_Fragment
      ...ResolvableServicePage_Materials_ResolvableServiceBaseInterface_Fragment
      ...ResolvableServicePage_Admin_ResolvableServiceBaseInterface_Fragment
    }
  `);

interface Props {
  readonly resolvableService: FragmentType<
    typeof ResolvableServicePage_Tabs_ResolvableServiceBaseInterface_Fragment
  >;
  readonly resolvableFieldSettings: Partial<ResolvableFieldSettings>;
}

export const ResolvableServiceTabs = ({
  resolvableService: pResolvableService,
  resolvableFieldSettings,
}: Readonly<Props>) => {
  const resolvableService = getFragmentData(
    ResolvableServicePage_Tabs_ResolvableServiceBaseInterface_Fragment,
    pResolvableService,
  );

  const { paths, getActiveTab } = useBuildPaths(
    {
      admin: PATH_RESOLVABLE_SERVICE_ADMIN,
      adminEdit: PATH_RESOLVABLE_SERVICE_ADMIN_EDIT,
      costs: PATH_RESOLVABLE_SERVICE_COSTS,
      costsEdit: PATH_RESOLVABLE_SERVICE_COSTS_EDIT,
      default: PATH_RESOLVABLE_SERVICE_DEFAULT,
      generalInfo: PATH_RESOLVABLE_SERVICE_GENERAL,
      generalInfoEdit: PATH_RESOLVABLE_SERVICE_GENERAL_EDIT,
      materials: PATH_RESOLVABLE_SERVICE_MATERIALS,
      materialsEdit: PATH_RESOLVABLE_SERVICE_MATERIALS_EDIT,
      quotationInfo: PATH_RESOLVABLE_SERVICE_QUOTATION_INFO,
      quotationInfoEdit: PATH_RESOLVABLE_SERVICE_QUOTATION_INFO_EDIT,
      versions: PATH_RESOLVABLE_SERVICE_VERSIONS,
    },
    { id: resolvableService._id },
    {
      admin: 'admin',
      adminEdit: 'admin',
      costs: 'costs',
      costsEdit: 'costs',
      default: 'generalInfo',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      materials: 'materials',
      materialsEdit: 'materials',
      quotationInfo: 'quotationInfo',
      quotationInfoEdit: 'quotationInfo',
      versions: 'versions',
    },
  );

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="resolvable-service-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo resolvableService={resolvableService} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'quotationInfo',
          label: 'Informations Devis',
          panel: (
            <QuotationInfo
              resolvableFieldSettings={resolvableFieldSettings}
              resolvableService={resolvableService}
            />
          ),
          to: paths.quotationInfo,
          value: 'quotationInfo',
        },
        {
          key: 'materials',
          label: 'Matériels Liés',
          panel: <Materials resolvableService={resolvableService} />,
          to: paths.materials,
          value: 'materials',
        },
        {
          key: 'costs',
          label: 'Coûts Liés',
          panel: <Costs resolvableService={resolvableService} />,
          to: paths.costs,
          value: 'costs',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions originalId={resolvableService._id} />,
          to: paths.versions,
          value: 'versions',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: <Admin resolvableService={resolvableService} />,
          to: paths.admin,
          value: 'admin',
        },
      ]}
      title="Onglets de l'écran de Fiche d'un Service"
    />
  );
};
